import type {
  InlineProductCardProps,
  RecommendedEssentialCardProps,
} from '@we-make-websites/ui-lib';
import {
  InlineProductCard,
  QuickAddToCartStates,
} from '@we-make-websites/ui-lib';
import { useRouter } from 'next/router';
import type { ComponentType } from 'react';
import { storeLocale } from '@/root/constants';
import { useCart } from 'components/cartContext/CartContext';
import { useCustomer } from 'components/customerContext/CustomerContext';
import { useFeatureFlags } from 'components/featureFlagsProvider/FeatureFlagsContext';

type ProductCardType = InlineProductCardProps | RecommendedEssentialCardProps;

type InlineProductCardConnectedProps = ProductCardType & {
  productCardComponent?: ComponentType<ProductCardType>;
  triggerCartDrawer?: boolean;
};

/**
 * Connected Inline Product Card.
 * - Wraps the InlineProductCard & RecommendedEssentialCard component and adds in extra context.
 * - Saves needing to import all the required context anywhere that the Product
 *   Card component is needed.
 */
export const InlineProductCardConnected = (
  props: InlineProductCardConnectedProps
) => {
  const {
    productCardComponent: ProductCardComponent = InlineProductCard,
    triggerCartDrawer = false,
  } = props;
  const { locale: routerLocale } = useRouter();
  const locale = storeLocale(routerLocale);

  const cartContext = useCart();
  if (cartContext === undefined)
    throw new Error('ProductCardConnected must be used within a CartContext');

  const featureFlagsContext = useFeatureFlags();
  if (featureFlagsContext === undefined)
    throw new Error(
      'ProductCardConnected must be used within a FeatureFlagsContext'
    );

  const { proCustomer } = useCustomer();

  const { cart, cartLineAddV2, cartLineAdd, cartLoading, quickAddedProductId } =
    cartContext;
  const { featureFlags } = featureFlagsContext;
  const { amazonCart, showPrice, cartEnabled } = featureFlags[locale];

  // Cart line items variant ids.
  // Used to check if product is already on cart or not.
  const cartVariantsIds = cart?.lines.map((line) => line.merchandise.id) ?? [];

  const variantAlreadyInCart = cartVariantsIds?.includes(
    props.product?.firstAvailableVariantId ?? ''
  );

  let quickAddButtonState = QuickAddToCartStates.Active;

  if (cartLoading && quickAddedProductId === props.product?.id) {
    quickAddButtonState = QuickAddToCartStates.Disabled;
  } else if (cartLoading) {
    quickAddButtonState = QuickAddToCartStates.Passive;
  }

  const mergedProductCardProps: InlineProductCardProps = {
    quickAddToCartState: quickAddButtonState,
    ...props,
    amazonCart,
    proCustomer,
    showPrice,
    cartEnabled,
  };

  return (
    <ProductCardComponent
      handleAdd={({ id, quantity }) =>
        triggerCartDrawer
          ? cartLineAdd({ id, quantity })
          : cartLineAddV2({ productId: props.product?.id || '', id, quantity })
      }
      {...mergedProductCardProps}
      variantAlreadyInCart={variantAlreadyInCart}
    />
  );
};
